import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Dialog, Select, TextField } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import { authClientFor, targetSession } from '../../identity/session';
import { automationGw, type Tenant } from '../api/client';
import { agPath, useAutomationTarget, useAutomationWrite } from '../shared';
import '../automation.css';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The gateway's tenants: a name the agents are grouped under, linked to the customer's AUTH tenant (G8). */
export function TenantsPage() {
  const target = useAutomationTarget();
  const api = useMemo(() => automationGw(target), [target]);
  const canWrite = useAutomationWrite();
  const toast = useNotice();
  const data = useAsync(async () => {
    const [tenants, agents, tunnels] = await Promise.all([api.tenants(), api.agents(), api.tunnels()]);
    return { tenants, agents, tunnels };
  }, [api]);
  // The AUTH tenants of this environment, to pick the link from (needs the AUTH session; else the id is typed).
  const authTenants = useAsync(
    async () =>
      targetSession(target).isSignedIn()
        ? (await authClientFor(target).tenants.list({ pageSize: 500 })).items
        : null,
    [target]
  );
  const authName = (id: string | null | undefined) =>
    id ? (authTenants.data?.find((t) => t.id === id)?.name ?? id) : null;
  const [editing, setEditing] = useState<{
    tenant: Tenant | null;
    name: string;
    authTenantId: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const d = data.data;
  const rows = (d?.tenants ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));
  const agentsOf = (id: string) => (d?.agents ?? []).filter((a) => a.tenantId === id);
  const online = (id: string) =>
    agentsOf(id).filter((a) => d?.tunnels.some((t) => t.clientDbId === a.id && t.isConnected)).length;

  const save = async () => {
    if (!editing) return;
    const name = editing.name.trim();
    if (!name) return setError('A name is required.');
    const link = editing.authTenantId.trim();
    if (link && !GUID.test(link)) return setError('The AUTH tenant must be a tenant id (GUID).');
    setBusy(true);
    setError(null);
    try {
      if (editing.tenant) await api.updateTenant(editing.tenant.id, { name, authTenantId: link || null });
      else {
        const created = await api.createTenant(name);
        if (link) await api.updateTenant(created.id, { name, authTenantId: link });
      }
      setEditing(null);
      toast.success(`Saved ${name}`);
      await data.reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (t: Tenant) => {
    try {
      await api.deleteTenant(t.id);
      toast.success(`Deleted ${t.name}`);
      await data.reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <Page
      title="Tenants"
      description="The gateway's tenants. Agents belong to one; a tenant with agents cannot be deleted. Link each to the customer's AUTH tenant so its agents can move to AUTH."
      actions={
        canWrite && (
          <Button
            size="sm"
            onClick={() => {
              setError(null);
              setEditing({ tenant: null, name: '', authTenantId: '' });
            }}
          >
            New tenant
          </Button>
        )
      }
    >
      {toast.element}
      <ErrorBox error={data.error} />
      <div className="nyk-card">
        <Loading loading={data.loading && !d} />
        {!!rows.length && (
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="ag-tenants">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>AUTH tenant</th>
                  <th className="nyk-ui-num">Agents</th>
                  <th className="nyk-ui-num">Connected</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} data-tenant={t.id}>
                    <td className="nyk-card-title">
                      <b>{t.name}</b>
                    </td>
                    <td data-label="AUTH tenant">
                      {t.authTenantId ? (
                        authName(t.authTenantId)
                      ) : (
                        <span className="nyk-ui-hint">not linked</span>
                      )}
                    </td>
                    <td className="nyk-ui-num" data-label="Agents">
                      <Link to={agPath(`/automation/agents?tenant=${t.id}`, target)}>
                        {agentsOf(t.id).length}
                      </Link>
                    </td>
                    <td className="nyk-ui-num" data-label="Connected">
                      {online(t.id)}
                    </td>
                    <td className="nyk-row-actions">
                      {canWrite && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setError(null);
                              setEditing({ tenant: t, name: t.name, authTenantId: t.authTenantId ?? '' });
                            }}
                          >
                            Edit
                          </Button>
                          {!agentsOf(t.id).length && (
                            <ConfirmButton
                              size="sm"
                              variant="ghost"
                              message={`Delete the tenant ${t.name}?`}
                              onConfirm={() => void remove(t)}
                            >
                              Delete
                            </ConfirmButton>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        )}
        {d && !rows.length && <EmptyState title="No tenants." />}
      </div>
      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.tenant ? `Edit ${editing.tenant.name}` : 'New tenant'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button loading={busy} onClick={() => void save()}>
              Save
            </Button>
          </>
        }
      >
        <div className="ag-form">
          <ErrorBox error={error} />
          <TextField
            label="Name"
            value={editing?.name ?? ''}
            onChange={(e) => editing && setEditing({ ...editing, name: e.target.value })}
          />
          {authTenants.data ? (
            <Select
              label="AUTH tenant"
              value={editing?.authTenantId ?? ''}
              onChange={(v) => editing && setEditing({ ...editing, authTenantId: v })}
              options={[
                { value: '', label: 'Not linked' },
                ...authTenants.data
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((t) => ({
                    value: t.id,
                    label: t.customerName ? `${t.name} (${t.customerName})` : t.name,
                  })),
              ]}
              hint="The customer's tenant in this environment's AUTH: its agents get their AUTH clients there."
            />
          ) : (
            <TextField
              label="AUTH tenant id"
              value={editing?.authTenantId ?? ''}
              onChange={(e) => editing && setEditing({ ...editing, authTenantId: e.target.value })}
              hint="Sign in to this environment's AUTH to pick it from a list."
            />
          )}
        </div>
      </Dialog>
    </Page>
  );
}
